import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

export const commercialPartnersSearchSchema = z.object({
  keyword: z.string().optional(),
  accountType: z.array(z.string()).optional(),
  keyAccntType: z.array(z.string()).optional(),
  commercialPartnerErp: z.array(z.string()).optional(),
  ...datatableSearchSchema.shape,
})

export type CommercialPartnersSearch = z.infer<typeof commercialPartnersSearchSchema>

export type CommercialPartners = Awaited<ReturnType<typeof getCommercialPartners>>
export type CommercialPartner = CommercialPartners['results'][number]

async function getCommercialPartners(search: CommercialPartnersSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['commercial-partners'].$get(
    { query: stringifyValues(search) },
    { init: { signal } },
  )
  return await res.json()
}

export function useCommercialPartners(queries: CommercialPartnersSearch) {
  return useQuery({
    queryKey: ['commercialPartners', queries],
    queryFn: ({ signal }) => getCommercialPartners(queries, signal),
  })
}

type DistinctHierarchyErpsOptions = {
  isActive?: boolean
  sapParentErpIsNull?: boolean
}

async function getDistinctHierarchyErps(
  options: DistinctHierarchyErpsOptions = {},
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['commercial-partners']['distinct-hierarchy-erps'].$get(
    { query: stringifyValues(options) },
    { init: { signal } },
  )
  return await res.json()
}

export function useDistinctHierarchyErps(options: DistinctHierarchyErpsOptions = {}) {
  return useQuery({
    queryKey: ['commercialPartnersDistinctHierarchyErps', options],
    queryFn: ({ signal }) => getDistinctHierarchyErps(options, signal),
  })
}

async function getDistinctAccountTypes(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['commercial-partners']['distinct-account-types'].$get(
    {},
    { init: { signal } },
  )
  return await res.json()
}

export function useDistinctAccountTypes() {
  return useQuery({
    queryKey: ['commercialPartnersDistinctAccountTypes'],
    queryFn: ({ signal }) => getDistinctAccountTypes(signal),
  })
}

async function getDistinctKeyAccntTypes(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['commercial-partners']['distinct-key-account-types'].$get(
    {},
    { init: { signal } },
  )
  return await res.json()
}

export function useDistinctKeyAccntTypes() {
  return useQuery({
    queryKey: ['commercialPartnersDistinctKeyAccntTypes'],
    queryFn: ({ signal }) => getDistinctKeyAccntTypes(signal),
  })
}
